import axios, { type AxiosError } from 'axios';

import { handleAxiosError } from '../errors/handleAxiosError';
import {
  applyDuplicateRequestCancellation,
  releaseInFlightRequest,
} from '../network/duplicateRequestGuard';
import {
  hasRecentNetworkFailures,
  recordNetworkFailure,
  recordNetworkSuccess,
} from '../network/networkState';
import {
  getTransportRetryDelay,
  shouldRecordNetworkFailure,
  shouldRetryTransportRequest,
} from '../network/retryConfig';
import {
  type AxiosFetchRequestConfig,
  readRequestMetadata,
  toAxiosConfigWithExtensions,
  withRequestMetadata,
  withUpdatedRetryMetadata,
} from '../network/types';

const REQUEST_TIMEOUT_MS = process.env['NEXT_PUBLIC_REQUEST_TIMEOUT_MS'];

/**
 * Fallback request timeout, used when `NEXT_PUBLIC_REQUEST_TIMEOUT_MS` is absent
 * or unusable. `apps/work-permit/.env` sets it, so this covers a build that does
 * not load that file.
 *
 * Axios waits indefinitely without one, and an unanswered request then leaves
 * its promise unsettled: the screen keeps a spinner with no error and no retry.
 * A timeout classifies as `timeout`, which is retried for idempotent methods and
 * renders `ConnectionErrorFallback` for the rest — both recoverable. Deliberately
 * looser than any environment should configure, since it only applies when
 * configuration is missing.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 60_000;

/**
 * `Number(undefined)` is `NaN` and `Number('')` is `0`, and axios reads either
 * as "wait forever", so both must fall back rather than pass through.
 */
export function resolveRequestTimeoutMs(
  configured: string | undefined,
): number {
  const parsed = Number(configured);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_REQUEST_TIMEOUT_MS;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

const httpClient = axios.create({
  timeout: resolveRequestTimeoutMs(REQUEST_TIMEOUT_MS),
});

httpClient.interceptors.request.use((config) => {
  const withMetadata = withRequestMetadata(config);
  return applyDuplicateRequestCancellation(withMetadata);
});

httpClient.interceptors.response.use(
  (response) => {
    releaseInFlightRequest(response.config);
    if (hasRecentNetworkFailures()) {
      recordNetworkSuccess();
    }
    return response;
  },
  async (error: AxiosError) => {
    releaseInFlightRequest(error.config);

    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      throw error;
    }

    const config = error.config;

    if (!config) {
      if (shouldRecordNetworkFailure(error)) {
        recordNetworkFailure();
      }
      throw error;
    }

    const configWithMetadata = readRequestMetadata(config)
      ? config
      : withRequestMetadata(config);

    if (shouldRetryTransportRequest(configWithMetadata, error)) {
      const retryCount =
        (readRequestMetadata(configWithMetadata)?.retryCount ?? 0) + 1;
      const nextConfig = withUpdatedRetryMetadata(
        configWithMetadata,
        retryCount,
      );

      await sleep(getTransportRetryDelay(retryCount - 1));
      return httpClient(nextConfig);
    }

    if (shouldRecordNetworkFailure(error)) {
      recordNetworkFailure();
    }
    throw error;
  },
);

export type FetchDataOptions = {
  apiUrl: string;
  pathParams?: Record<string, string | number>;
  searchParams?: Record<string, string | number | boolean>;
  body?: unknown;
  disableCache?: boolean;
} & AxiosFetchRequestConfig;

function resolveRequestMethod(
  method: AxiosFetchRequestConfig['method'] | undefined,
  body: unknown,
): NonNullable<AxiosFetchRequestConfig['method']> {
  if (method) {
    return method;
  }
  return body ? 'post' : 'get';
}

/**
 * Central HTTP client for app and workflow API calls.
 * Includes transport retries, duplicate-request cancellation, and normalized errors.
 */
export const axiosFetch = async <T = unknown>({
  apiUrl,
  searchParams,
  body,
  disableCache,
  enableRetry,
  cancelDuplicate,
  method,
  ...options
}: FetchDataOptions): Promise<T> => {
  try {
    const headers = disableCache
      ? { ...options.headers, 'Cache-Control': 'no-cache' }
      : options.headers;

    const requestConfig = toAxiosConfigWithExtensions({
      method: resolveRequestMethod(method, body),
      url: apiUrl,
      params: searchParams,
      data: body,
      headers,
      enableRetry,
      cancelDuplicate,
      ...options,
    });

    const response = await httpClient<T>(requestConfig);

    return response.data;
  } catch (error) {
    // NOTE: Don't report errors here - they're already reported by baseSafeRouteHandler
    // This is just a transport layer, error reporting happens at the API route handler level

    // Parses InSafe numeric codes and external string codes (e.g. SAP `"401"`) via handleAxiosError
    if (axios.isCancel(error)) {
      throw error;
    }
    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
      throw error;
    }
    return handleAxiosError(error, apiUrl);
  }
};
